import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const accesoAdmin = params.get("admin") === "1";

  const [esRegistro, setEsRegistro] = useState(false);
  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [cargando, setCargando] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [mensajeExito, setMensajeExito] = useState("");

  const [usuarioActual, setUsuarioActual] = useState(null);
  const [codigoInstalacion, setCodigoInstalacion] = useState("");
  const [mensajeAdmin, setMensajeAdmin] = useState("");
  const [procesandoAdmin, setProcesandoAdmin] = useState(false);

  const verificarYCrearPerfilEnFirestore = async (user) => {
    try {
      const userDocRef = doc(db, "usuarios", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          nombreCompleto: user.displayName || "Cliente Mítico",
          telefono: "",
          direcciones: [],
          fechaRegistro: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error al gestionar perfil:", error);
    }
  };

  const verificarSiEsAdmin = async (user) => {
    if (!user?.email) return false;

    const adminsRef = collection(db, "admins");

    const q = query(
      adminsRef,
      where("email", "==", user.email),
      where("activo", "==", true),
    );

    const snapshot = await getDocs(q);

    return !snapshot.empty;
  };

  useEffect(() => {
    const desuscribir = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUsuarioActual(null);
        return;
      }

      setUsuarioActual(user);
      verificarYCrearPerfilEnFirestore(user);

      if (accesoAdmin) {
        const esAdmin = await verificarSiEsAdmin(user);

        if (esAdmin) {
          navigate("/admin");
        }

        return;
      }

      navigate("/");
    });

    return () => desuscribir();
  }, [navigate, accesoAdmin]);

  const handleAuthCorreo = async (e) => {
    e.preventDefault();

    if (!correo || !password) {
      setErrorMensaje("⚠️ Por favor llena todos los campos.");
      return;
    }

    setCargando(true);
    setErrorMensaje("");

    try {
      let userCredential;

      if (esRegistro) {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          correo,
          password,
        );
      } else {
        userCredential = await signInWithEmailAndPassword(
          auth,
          correo,
          password,
        );
      }

      verificarYCrearPerfilEnFirestore(userCredential.user);

      if (accesoAdmin) {
        const esAdmin = await verificarSiEsAdmin(userCredential.user);

        if (esAdmin) {
          navigate("/admin");
        } else {
          setUsuarioActual(userCredential.user);
          setErrorMensaje("");
        }

        setCargando(false);
        return;
      }

      navigate("/");
    } catch (error) {
      console.error(error);
      setCargando(false);

      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found" ||
        error.code === "auth/invalid-credential"
      ) {
        setErrorMensaje("❌ Correo o contraseña incorrectos.");
      } else if (error.code === "auth/email-already-in-use") {
        setErrorMensaje(
          "⚠️ Este correo ya está registrado. Intenta iniciar sesión.",
        );
      } else if (error.code === "auth/weak-password") {
        setErrorMensaje("⚠️ La contraseña debe tener al menos 6 caracteres.");
      } else {
        setErrorMensaje("❌ Hubo un error al intentar autenticar.");
      }
    }
  };

  const handleLoginGoogle = async () => {
    setCargando(true);
    setErrorMensaje("");

    const proveedorGoogle = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, proveedorGoogle);

      verificarYCrearPerfilEnFirestore(result.user);

      if (accesoAdmin) {
        const esAdmin = await verificarSiEsAdmin(result.user);

        if (esAdmin) {
          navigate("/admin");
        } else {
          setUsuarioActual(result.user);
          setErrorMensaje("");
        }

        setCargando(false);
        return;
      }

      navigate("/");
    } catch (error) {
      console.error("Error en Google Auth:", error);
      setCargando(false);

      if (
        error.code !== "auth/popup-closed-by-user" &&
        error.code !== "auth/cancelled-popup-request"
      ) {
        setErrorMensaje(
          "❌ No se pudo completar el inicio de sesión con Google.",
        );
      }
    }
  };

  const handleRecuperarPassword = async () => {
    if (!correo.trim()) {
      setErrorMensaje(
        "⚠️ Por favor escribe tu correo arriba para enviarte el enlace de recuperación.",
      );
      setMensajeExito("");
      return;
    }

    setCargando(true);
    setErrorMensaje("");
    setMensajeExito("");

    try {
      await sendPasswordResetEmail(auth, correo.trim());
      setMensajeExito(
        "✅ Te hemos enviado un enlace a tu correo para restablecer tu contraseña. Revisa tu bandeja de entrada o spam.",
      );
    } catch (error) {
      console.error("Error al recuperar contraseña:", error);
      if (error.code === "auth/user-not-found") {
        setErrorMensaje(
          "❌ No existe ninguna cuenta registrada con este correo.",
        );
      } else if (error.code === "auth/invalid-email") {
        setErrorMensaje("⚠️ El formato del correo electrónico no es válido.");
      } else {
        setErrorMensaje(
          "❌ No se pudo enviar el correo. Intenta de nuevo más tarde.",
        );
      }
    } finally {
      setCargando(false);
    }
  };

  const handleCrearPrimerAdmin = async () => {
    if (!usuarioActual) {
      setMensajeAdmin("⚠️ Primero inicia sesión.");
      return;
    }

    if (!codigoInstalacion.trim()) {
      setMensajeAdmin("⚠️ Ingresa el código de instalación.");
      return;
    }

    setProcesandoAdmin(true);
    setMensajeAdmin("Procesando solicitud...");

    try {
      const solicitudRef = await addDoc(collection(db, "solicitudesAdmin"), {
        uid: usuarioActual.uid,
        email: usuarioActual.email,
        nombre: usuarioActual.displayName || "Administrador principal",
        codigoInstalacion: codigoInstalacion.trim(),
        estado: "pendiente",
        fechaSolicitud: new Date().toISOString(),
      });

      const unsubscribe = onSnapshot(solicitudRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();

        if (data.estado === "aprobada") {
          unsubscribe();
          setProcesandoAdmin(false);
          setMensajeAdmin("✅ Administrador principal creado correctamente.");
          navigate("/admin");
        }

        if (data.estado === "rechazada") {
          unsubscribe();
          setProcesandoAdmin(false);
          setMensajeAdmin(`❌ ${data.motivo || "Solicitud rechazada."}`);
        }

        if (data.estado === "error") {
          unsubscribe();
          setProcesandoAdmin(false);
          setMensajeAdmin(
            `❌ ${data.motivo || "Error procesando la solicitud."}`,
          );
        }
      });
    } catch (error) {
      console.error("Error creando solicitud admin:", error);
      setProcesandoAdmin(false);
      setMensajeAdmin("❌ No se pudo enviar la solicitud de administrador.");
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f4f4f6",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: "-apple-system, sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: "400px",
          borderRadius: "24px",
          padding: "32px",
          boxSizing: "border-box",
          boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
          textAlign: "center",
        }}
      >
        <img
          src="/logo-mitico.png"
          alt="Mítico Amorcito"
          style={{
            maxWidth: "150px",
            marginBottom: "10px",
            objectFit: "contain",
          }}
        />

        <h2
          style={{
            margin: "0 0 8px 0",
            fontSize: "22px",
            fontWeight: "900",
            letterSpacing: "-0.5px",
          }}
        >
          {accesoAdmin
            ? "Acceso Administrativo"
            : esRegistro
              ? "Crear cuenta en Mítico"
              : "¡Qué bueno verte!"}
        </h2>

        <p style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#666" }}>
          {accesoAdmin
            ? "Inicia sesión con la cuenta autorizada del negocio."
            : esRegistro
              ? "Regístrate para guardar tus domicilios y pedir rápido."
              : "Inicia sesión para continuar con tu orden."}
        </p>

        {errorMensaje && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fce8e6",
              color: "#c5221f",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: "500",
              marginBottom: "16px",
              textAlign: "left",
            }}
          >
            {errorMensaje}
          </div>
        )}

        {mensajeExito && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#e6f4ea",
              color: "#137333",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: "500",
              marginBottom: "16px",
              textAlign: "left",
            }}
          >
            {mensajeExito}
          </div>
        )}

        <form
          onSubmit={handleAuthCorreo}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            textAlign: "left",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Correo Electrónico
            </label>
            <input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="tu@correo.com"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
                fontSize: "14px",
              }}
            />
          </div>

          {!esRegistro && (
            <div style={{ textAlign: "right" }}>
              <button
                type="button"
                onClick={handleRecuperarPassword}
                disabled={cargando}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1a73e8",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={cargando}
            style={{
              width: "100%",
              backgroundColor: "#111",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "14px",
              fontSize: "15px",
              fontWeight: "700",
              cursor: "pointer",
              marginTop: "10px",
              transition: "0.2s",
            }}
          >
            {cargando
              ? "Procesando..."
              : accesoAdmin
                ? "Entrar al Administrador"
                : esRegistro
                  ? "Registrarme Oficialmente"
                  : "Entrar a la Tienda"}
          </button>
        </form>

        <div
          style={{
            margin: "20px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            color: "#999",
            fontSize: "12px",
          }}
        >
          <hr
            style={{ flex: 1, border: "none", borderTop: "1px solid #eee" }}
          />
          O
          <hr
            style={{ flex: 1, border: "none", borderTop: "1px solid #eee" }}
          />
        </div>

        <button
          onClick={handleLoginGoogle}
          disabled={cargando}
          style={{
            width: "100%",
            backgroundColor: "#fff",
            color: "#333",
            border: "1px solid #ddd",
            borderRadius: "12px",
            padding: "12px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            transition: "0.2s",
          }}
        >
          <svg style={{ width: "18px", height: "18px" }} viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
          Continuar con Google
        </button>

        {accesoAdmin && usuarioActual && (
          <div
            style={{
              marginTop: "22px",
              padding: "16px",
              borderRadius: "16px",
              backgroundColor: "#f8f8f8",
              textAlign: "left",
              border: "1px solid #eee",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "15px",
                fontWeight: "900",
              }}
            >
              Crear administrador principal
            </h3>

            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "12px",
                color: "#666",
                lineHeight: "1.4",
              }}
            >
              Solo funciona si todavía no existe ningún administrador en esta
              instalación.
            </p>

            <input
              type="password"
              value={codigoInstalacion}
              onChange={(e) => setCodigoInstalacion(e.target.value)}
              placeholder="Código de instalación"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
                fontSize: "14px",
                marginBottom: "10px",
              }}
            />

            <button
              type="button"
              onClick={handleCrearPrimerAdmin}
              disabled={procesandoAdmin}
              style={{
                width: "100%",
                backgroundColor: "#111",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                padding: "12px",
                fontSize: "14px",
                fontWeight: "800",
                cursor: "pointer",
              }}
            >
              {procesandoAdmin
                ? "Validando..."
                : "Crear administrador principal"}
            </button>

            {mensajeAdmin && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: mensajeAdmin.startsWith("✅") ? "#137333" : "#c5221f",
                  lineHeight: "1.4",
                }}
              >
                {mensajeAdmin}
              </div>
            )}
          </div>
        )}

        {!accesoAdmin && (
          <p style={{ margin: "24px 0 0 0", fontSize: "13px", color: "#555" }}>
            {esRegistro ? "¿Ya tienes una cuenta?" : "¿Eres nuevo en Mítico?"}{" "}
            <span
              onClick={() => {
                setEsRegistro(!esRegistro);
                setErrorMensaje("");
              }}
              style={{
                color: "#1a73e8",
                fontWeight: "700",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {esRegistro ? "Inicia sesión aquí" : "Regístrate gratis aquí"}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
